import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CustomerPricing, DiameterPrice, PriceList, ShapeException, fillMissing } from "../../utils/supplier-pricing-engine";

export interface SupplierPricingState {
  status: "idle" | "loading" | "ready" | "saving" | "error";
  saved: PriceList | null;
  draft: PriceList | null;
  dirty: boolean;
  customLists: CustomerPricing[];
  customerPricing: Record<number, CustomerPricing>;
  history: any[];
}

const initialState: SupplierPricingState = {
  status: "idle",
  saved: null,
  draft: null,
  dirty: false,
  customLists: [],
  customerPricing: {},
  history: [],
};

const clone = <T,>(o: T): T => JSON.parse(JSON.stringify(o));

export const supplierPricingSlice = createSlice({
  name: "supplierPricing",
  initialState,
  reducers: {
    setPricingStatus: (state, action: PayloadAction<SupplierPricingState["status"]>) => {
      state.status = action.payload;
    },
    setPriceList: (state, action: PayloadAction<PriceList>) => {
      state.saved = action.payload;
      state.draft = clone(action.payload);
      state.dirty = false;
      state.status = "ready";
    },
    discardDraft: (state) => {
      state.draft = state.saved ? clone(state.saved) : null;
      state.dirty = false;
    },
    setDraftField: (state, action: PayloadAction<{ key: keyof PriceList; value: any }>) => {
      if (!state.draft) return;
      (state.draft as any)[action.payload.key] = action.payload.value;
      state.dirty = true;
    },
    setDiameterPrice: (state, action: PayloadAction<{ diameter: number; column: keyof DiameterPrice; value: string }>) => {
      const row = state.draft?.diameterPrices.find((r) => r.diameter === action.payload.diameter);
      if (!row) return;
      (row as any)[action.payload.column] = action.payload.value === "" ? null : Number(action.payload.value);
      state.dirty = true;
    },
    addDiameter: (state, action: PayloadAction<number>) => {
      if (!state.draft || state.draft.diameterPrices.some((r) => r.diameter === action.payload)) return;
      state.draft.diameterPrices.push({ diameter: action.payload, ribbed: null, smooth: null, weldableExtra: null });
      state.draft.diameterPrices.sort((a, b) => a.diameter - b.diameter);
      state.dirty = true;
    },
    removeDiameter: (state, action: PayloadAction<number>) => {
      if (!state.draft) return;
      // Diameter rows remain present; clear a value to edit pricing instead.
    },
    fillMissingColumn: (state, action: PayloadAction<"ribbed" | "smooth" | "weldableExtra">) => {
      if (!state.draft) return;
      state.draft.diameterPrices = fillMissing(state.draft.diameterPrices, action.payload);
      state.dirty = true;
    },
    upsertShapeException: (state, action: PayloadAction<ShapeException>) => {
      [state.saved, state.draft].forEach((pl) => {
        if (!pl) return;
        const i = pl.shapeExceptions.findIndex((e) => e.id === action.payload.id);
        if (i >= 0) pl.shapeExceptions[i] = action.payload;
        else pl.shapeExceptions.push(action.payload);
      });
    },
    removeShapeException: (state, action: PayloadAction<number>) => {
      [state.saved, state.draft].forEach((pl) => {
        if (pl) pl.shapeExceptions = pl.shapeExceptions.filter((e) => e.id !== action.payload);
      });
    },
    setCustomLists: (state, action: PayloadAction<CustomerPricing[]>) => {
      state.customLists = action.payload;
    },
    setCustomerPricing: (state, action: PayloadAction<CustomerPricing>) => {
      const cp = action.payload;
      state.customerPricing[cp.customer] = cp;
      state.customLists = [...state.customLists.filter((c) => c.customer !== cp.customer), ...(cp.mode === "custom" ? [cp] : [])];
    },
    setHistory: (state, action: PayloadAction<any[]>) => {
      state.history = action.payload;
    },
  },
});

export const {
  setPricingStatus,
  setPriceList,
  discardDraft,
  setDraftField,
  setDiameterPrice,
  addDiameter,
  removeDiameter,
  fillMissingColumn,
  upsertShapeException,
  removeShapeException,
  setCustomLists,
  setCustomerPricing,
  setHistory,
} = supplierPricingSlice.actions;

export default supplierPricingSlice.reducer;
